"use client";

import React, { useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	Box,
	TextField,
	Button,
	Typography,
	IconButton,
	Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
	useMentorConsultationMutation,
	MentorConsultationInput,
} from "../../operations/mutations";
import {
	MentorConsultationSchema,
	MentorConsultationFormData,
} from "./validations";
import { z } from "zod";

interface MentorConsultationModalProps {
	open: boolean;
	onClose: () => void;
}

const MentorConsultationModal: React.FC<MentorConsultationModalProps> = ({
	open,
	onClose,
}) => {
	const [formData, setFormData] = useState<MentorConsultationFormData>({
		representativeName: "",
		email: "",
		phoneNumber: "",
		consultationContent: "",
	});
	const [errors, setErrors] = useState<
		Partial<Record<keyof MentorConsultationFormData, string>>
	>({});
	const [showSuccess, setShowSuccess] = useState(false);

	const mentorConsultationMutation = useMentorConsultationMutation();

	const validateForm = (): boolean => {
		try {
			MentorConsultationSchema.parse(formData);
			setErrors({});
			return true;
		} catch (error) {
			if (error instanceof z.ZodError) {
				const newErrors: Partial<
					Record<keyof MentorConsultationFormData, string>
				> = {};
				error.errors.forEach((err) => {
					if (err.path.length > 0) {
						const field = err.path[0] as keyof MentorConsultationFormData;
						newErrors[field] = err.message;
					}
				});
				setErrors(newErrors);
			}
			return false;
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		try {
			await mentorConsultationMutation.mutateAsync(formData);
			setShowSuccess(true);
			setFormData({
				representativeName: "",
				email: "",
				phoneNumber: "",
				consultationContent: "",
			});
			setErrors({});
		} catch (error) {
			console.error("Error submitting mentor consultation:", error);
		}
	};

	const handleCancel = () => {
		setFormData({
			representativeName: "",
			email: "",
			phoneNumber: "",
			consultationContent: "",
		});
		setErrors({});
		setShowSuccess(false);
		onClose();
	};

	const handleCloseSuccess = () => {
		setShowSuccess(false);
		setFormData({
			representativeName: "",
			email: "",
			phoneNumber: "",
			consultationContent: "",
		});
		setErrors({});
		onClose();
	};

	const handleInputChange = (
		field: keyof MentorConsultationFormData,
		value: string,
	) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: undefined }));
		}
	};

	if (showSuccess) {
		return (
			<Dialog
				open={open}
				onClose={handleCloseSuccess}
				aria-labelledby="success-modal"
				maxWidth={false}
				PaperProps={{
					sx: {
						display: "flex",
						width: "466px",
						height: "fit-content",
						flexDirection: "column",
						alignItems: "center",
						flexShrink: 0,
						borderRadius: "16px",
						background: "#FFF",
						boxShadow: "-40px 40px 80px -8px rgba(0, 0, 0, 0.24)",
						padding: 0,
						overflow: "hidden",
					},
				}}
			>
				{/* Background Section */}
				<Box
					sx={{
						position: "relative",
						width: "466px",
						height: "191px",
						flexShrink: 0,
						backgroundImage: `url('/assets/images/modal-background.png')`,
						backgroundSize: "cover",
						backgroundPosition: "center",
						backgroundRepeat: "no-repeat",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "24px",
					}}
				>
					{/* Close Button */}
					<IconButton
						onClick={handleCloseSuccess}
						sx={{
							position: "absolute",
							top: 16,
							right: 16,
							zIndex: 10,
							backgroundColor: "rgba(255, 255, 255, 0.2)",
							color: "white",
							width: "32px",
							height: "32px",
							"&:hover": {
								backgroundColor: "rgba(255, 255, 255, 0.3)",
							},
						}}
					>
						<CloseIcon sx={{ fontSize: "20px" }} />
					</IconButton>

					{/* Success Title */}
					<h1
						style={{
							color: "#FFF",
							fontSize: "48px",
							fontStyle: "normal",
							fontWeight: 600,
							lineHeight: "100%",
							margin: 0,
							zIndex: 5,
							alignSelf: "center",
						}}
					>
						Gửi yêu cầu
						<br />
						thành công!
					</h1>
				</Box>

				{/* Text Section */}
				<Box
					sx={{
						display: "flex",
						padding: "24px",
						flexDirection: "column",
						alignItems: "center",
						gap: "24px",
						alignSelf: "stretch",
						flex: 1,
						justifyContent: "center",
					}}
				>
					{/* Success Message */}
					<p
						style={{
							color: "#212B36",
							fontSize: "16px",
							fontStyle: "normal",
							fontWeight: 400,
							lineHeight: "24px",
							margin: 0,
							textAlign: "center",
						}}
					>
						Chúng tôi sẽ liên hệ với bạn trong thời gian sớm nhất.
					</p>
				</Box>
			</Dialog>
		);
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="h6">Đăng ký tư vấn Mentor</Typography>
					<IconButton onClick={handleCancel}>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
					<TextField
						fullWidth
						label="Tên người đại diện liên hệ"
						value={formData.representativeName}
						onChange={(e) =>
							handleInputChange("representativeName", e.target.value)
						}
						error={!!errors.representativeName}
						helperText={errors.representativeName}
						margin="normal"
						required
					/>

					<TextField
						fullWidth
						label="Email"
						type="email"
						value={formData.email}
						onChange={(e) => handleInputChange("email", e.target.value)}
						error={!!errors.email}
						helperText={errors.email}
						margin="normal"
						required
					/>

					<TextField
						fullWidth
						label="Số điện thoại"
						value={formData.phoneNumber}
						onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
						error={!!errors.phoneNumber}
						helperText={errors.phoneNumber}
						margin="normal"
						required
					/>

					<TextField
						fullWidth
						label="Nội dung cần tư vấn"
						multiline
						rows={4}
						value={formData.consultationContent}
						onChange={(e) =>
							handleInputChange("consultationContent", e.target.value)
						}
						error={!!errors.consultationContent}
						helperText={errors.consultationContent}
						margin="normal"
						required
					/>

					<Box display="flex" justifyContent="flex-end" gap={2} sx={{ my: 3 }}>
						<Button
							type="button"
							variant="outlined"
							onClick={handleCancel}
							disabled={mentorConsultationMutation.isPending}
						>
							Hủy
						</Button>
						<Button
							type="submit"
							variant="contained"
							disabled={mentorConsultationMutation.isPending}
						>
							{mentorConsultationMutation.isPending
								? "Đang gửi..."
								: "Gửi yêu cầu"}
						</Button>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default MentorConsultationModal;
